<script setup lang="ts">
export interface EventInfoMetaTitleProps {
  /**
   * The title of the event.
   */
  title?: string;

  /**
   * The name of the team associated with the event.
   */
  teamName?: string;

  /**
   * The type of sport for the event.
   */
  sportType?: TSportType;

  /**
   * Indicates whether the event is of an "other" game type.
   */
  isOtherGameType: boolean;

  /**
   * Indicates whether the event is of a practice game type.
   */
  isPracticeGameType: boolean;

  /**
   * Indicates whether the event has a clip.
   */
  isClip?: boolean;

  /**
   * Indicates whether the event is a highlight.
   */
  isHighlight?: boolean;

  /**
   * Indicates whether the event has a badge.
   */
  hasBadge?: boolean;

  /**
   * Indicates whether the event has associated statistics.
   */
  hasStatistics?: boolean;
  /**
   * Indicate gender of the event
   */
  gender?: TGender | null;
}

const props = withDefaults(defineProps<EventInfoMetaTitleProps>(), {
  sportType: "other",
});
const { t } = useI18n();

const eventTitle = computed(() => {
  if (props.isOtherGameType)
    return t("labels.gameType_labels.other");
  if (props.isPracticeGameType)
    return `${t("labels.gameType_labels.practice")} • ${props.teamName} ${t("labels.gameType_labels.training")}`;

  return props.title;
});
const localizedSportType = computed(() => {
  if (!props.sportType)
    return t("sport_types.other");
  return t(`sport_types.${props.sportType}`);
});
</script>

<template>
  <div class="mt-4 flex justify-between">
    <div class="flex items-center gap-1">
      <div>
        <IconGameTypeOther v-if="props.isOtherGameType" :width="8" :height="8" />
        <IconGameTypePractice v-else-if="props.isPracticeGameType" :width="8" :height="8" />
        <BaseChip v-else-if="isHighlight" class="flex items-center gap-2 !px-3 !py-1 bg-[var(--base-background-color)]">
          <IconHighlight :width="8" :height="8" />
          <p class="text-[11px]">
            {{ t('labels.highlights', 1) }}
          </p>
        </BaseChip>
        <BaseChip v-else-if="isClip" class="flex items-center gap-2 !px-3 !py-1 bg-[var(--base-background-color)]">
          <IconClip :width="8" :height="8" />
          <p class="text-[11px]">
            {{ t('labels.clip', 1) }}
          </p>
        </BaseChip>
      </div>
      <p class="text-caption-medium text-secondary line-clamp-1 mr-2" :title="props.title">
        {{ eventTitle }}
      </p>
    </div>
    <div class="flex items-center gap-3">
      <IconGender
        v-if="props.gender" v-tooltip="t(`labels.gender_labels.${props.gender}`)" :gender="props.gender"
      />
      <IconStatistic v-if="hasStatistics" v-tooltip="t('labels.statistics')" class="iconStatistic" />
      <IconSportType v-tooltip="localizedSportType" class="sportTypeIcon" :sport-type="props.sportType" />
    </div>
  </div>
</template>

<style lang="scss">
 .iconStatistic:hover path {
      transition: all 0.5s;
      fill: var(--base-primary-color);
    }
</style>
